<template>
  <div class="page-image" :style="boxStyle">
    <img v-if="page.thumbnailUrl" :src="page.thumbnailUrl" class="layer" alt="" @load="onThumbnailLoad">
    <img ref="full" :src="page.pictureUrl" class="layer full" :class="{ shown: loaded && reveal, instant }" :alt="page.notes || ''"
         @load="onLoad">
    <slot />
  </div>
</template>

<script>
// Shows the page's thumbnail stretched to the page's size until the full image loads, then fades the full image in on
// top. The box is sized from the image's aspect ratio (square until known, since most pages are 12x12), so nothing
// jumps around and both layers always line up. Key this component by page id so a new page gets fresh state.
//
// The parent can hold off showing the full image with `reveal`, e.g. until both pages of a spread are loaded, so you
// don't see one sharp page next to a blurry one.
export default {
  props: {
    page: { type: Object, required: true },
    reveal: { type: Boolean, default: true },
    // In pixels. Optional.
    maxHeight: Number,
  },
  data() {
    return {
      aspectRatio: 1,
      loaded: false,
      // True when the image was already in the browser cache, so it shouldn't fade in.
      instant: false,
    }
  },
  mounted() {
    const img = this.$refs.full
    if (img.complete && img.naturalWidth) {
      this.instant = true
      this.onLoad({ target: img })
    }
  },
  computed: {
    boxStyle() {
      const style = { aspectRatio: String(this.aspectRatio) }
      if (this.maxHeight) {
        style.maxWidth = (this.maxHeight * this.aspectRatio) + 'px'
      }
      return style
    },
  },
  methods: {
    onThumbnailLoad(event) {
      if (!this.loaded) {
        this.setAspectRatio(event.target)
      }
    },
    onLoad(event) {
      if (this.loaded) {
        return
      }
      this.setAspectRatio(event.target)
      this.loaded = true
      this.$emit('load', this.page)
    },
    setAspectRatio(img) {
      if (img.naturalWidth && img.naturalHeight) {
        this.aspectRatio = img.naturalWidth / img.naturalHeight
        this.$emit('aspect-ratio', { page: this.page, aspectRatio: this.aspectRatio })
      }
    },
  },
}
</script>

<style lang="stylus" scoped>
.page-image
  position relative
  width 100%

  .layer
    position absolute
    top 0
    left 0
    width 100%
    height 100%
    max-width none

  .full
    opacity 0
    transition opacity .2s

    &.shown
      opacity 1

    &.instant
      transition none
</style>
